import type { Me, VoicemailSummary } from '@smarton-pbx/shared';
import { Loader2, MessageSquare, PhoneOutgoing, UserPlus, Voicemail as VoicemailIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { Dialog, EmptyState, Loading, Notice, PageHeader, Pill } from '../components/ui';
import { t } from '../i18n';
import { formatNumber } from '../lib/calls';
import { useLoad } from '../lib/hooks';
import { when } from './Calls';
import { ContactForm } from './contacts';

/** What the caller said (once written down), and the player. */
export function VoicemailMessage({ message, onPlay }: { message: VoicemailSummary; onPlay?: () => void }) {
  return (
    <>
      {message.transcriptStatus === 'PENDING' ? (
        <p className="subtle row" style={{ gap: '0.4rem', fontSize: '0.9rem' }}>
          <Loader2 size={14} className="spin" aria-hidden /> {t('Writing down the message…')}
        </p>
      ) : message.transcript ? (
        <blockquote className="voicemail-transcript" data-testid="voicemail-transcript">
          {message.transcript}
        </blockquote>
      ) : null}
      <audio
        controls
        preload="none"
        src={api.voicemailAudioUrl(message.id)}
        onPlay={onPlay}
        aria-label={t('Message from {name}', { name: message.contactName ?? message.fromNumber })}
      />
    </>
  );
}

export function Voicemail({ me }: { me: Me }) {
  const messages = useLoad(api.listVoicemails);
  // Messages played here since the list loaded.
  const [played, setPlayed] = useState<Set<string>>(new Set());
  // A caller being saved as a contact.
  const [saving, setSaving] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <div className="stack">
      <PageHeader title={t('Voicemail')} description={t('Messages left when nobody answered your number.')} />
      {messages.error && <Notice kind="error">{messages.error}</Notice>}
      {!messages.data ? (
        <Loading />
      ) : messages.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={VoicemailIcon} title={t('No messages')}>
            <p>{t('When a caller leaves a message, it appears here.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card">
          {messages.data.map((message: VoicemailSummary) => {
            const isNew = !message.listened && !played.has(message.id);
            return (
              <div key={message.id} className="voicemail" data-testid="voicemail">
                <div className="voicemail-head">
                  <span className="grow">
                    {message.contactId ? (
                      <Link to={`/contacts/${message.contactId}`} className="plain-link" style={{ fontWeight: 600 }} data-testid="voicemail-contact">
                        {message.contactName}
                      </Link>
                    ) : (
                      <span className="tabular" style={{ fontWeight: 600 }}>
                        {formatNumber(message.fromNumber)}
                      </span>
                    )}
                    {isNew && (
                      <>
                        {' '}
                        <Pill kind="brand">{t('New')}</Pill>
                      </>
                    )}
                    <span className="subtle" style={{ display: 'block', fontSize: '0.85rem' }}>
                      {message.contactId && <span className="tabular">{formatNumber(message.fromNumber)} · </span>}
                      <Link to={`/calls/${message.callId}`} className="plain-link">
                        {when(message.createdAt)}
                      </Link>{' '}
                      · {message.durationSeconds} s
                    </span>
                  </span>
                  {!message.contactId && message.fromNumber.startsWith('+') && (
                    <button type="button" className="secondary small" onClick={() => setSaving(message.fromNumber)} data-testid="voicemail-save-contact">
                      <UserPlus size={15} aria-hidden /> {t('Save contact')}
                    </button>
                  )}
                  {message.fromNumber.startsWith('+') && (
                    <Link to={`/texts/new?to=${encodeURIComponent(message.fromNumber)}`} className="button secondary small">
                      <MessageSquare size={15} aria-hidden /> {t('Text')}
                    </Link>
                  )}
                  <button
                    type="button"
                    className="secondary small"
                    onClick={() => void navigate(`/?number=${encodeURIComponent(message.fromNumber)}`)}
                    title={t('Put the number in the dialer')}
                  >
                    <PhoneOutgoing size={15} aria-hidden /> {t('Call back')}
                  </button>
                </div>
                <VoicemailMessage message={message} onPlay={() => setPlayed((ids) => new Set(ids).add(message.id))} />
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={saving !== null} onClose={() => setSaving(null)} title={t('New contact')} wide>
        {saving !== null && (
          <ContactForm
            me={me}
            contact={null}
            initialNumber={saving}
            onCancel={() => setSaving(null)}
            onSaved={() => {
              setSaving(null);
              messages.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}
